import React from 'react';
import { Accordion } from '@dropbox/dig-components/accordion';
import styles from './white-labeling-app.module.scss';
import { Text } from '@dropbox/dig-components/typography';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import type { ColorConfig } from './preset-utils';
import { parseUploadedColorPresetJson, validateKeys } from './preset-utils';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';

interface AccordionColorPickerSectionItem<PropertyId extends string> {
  id: PropertyId;
  label: string;
}
export interface AccordionColorPickerSection<PropertyId extends string> {
  sectionId: string;
  sectionLabel: string;
  items: AccordionColorPickerSectionItem<PropertyId>[];
}
export interface AccordionColorPickerProps<PropertyId extends string> {
  sections: AccordionColorPickerSection<PropertyId>[];
  getValue: (id: PropertyId) => string;
  setValue: (id: PropertyId, value: string) => void;
  setLogoUrl: (url: string) => void;
  onImportColorPreset: (preset: ColorConfig) => void;
}

const messages = defineMessages({
  fileInputMessage: {
    id: '',
    description: 'File input label',
    defaultMessage: 'white labeling logo',
  },
  jsonFileInputMessage: {
    id: '',
    description: 'Json file input label',
    defaultMessage: 'white labeling colors json',
  },
});

function AccordionColorPicker<PropertyId extends string>(
  props: AccordionColorPickerProps<PropertyId>,
) {
  const intl = useIntl();
  const [importPresetError, setImportPresetError] = React.useState<
    string | null
  >(null);
  const [importUnrecognisedNotice, setImportUnrecognisedNotice] =
    React.useState<string | null>(null);
  const [expandedPanels, setExpandedPanels] = React.useState<string[]>([
    'logo',
    'preset',
    ...props.sections.map((s) => s.sectionId),
  ]);

  const isColorsImportEnabled = useFeatureFlag(
    'sign_core_2026_04_02_white_labeling_colors_import',
  );

  const handleClick = (itemId: string) => {
    setExpandedPanels((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return prev.concat([itemId]);
      }
    });
  };

  const saveLogo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      props.setLogoUrl(URL.createObjectURL(file));
    }
  };

  const loadColorPresetJson = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const input = event.target;
    setImportPresetError(null);
    setImportUnrecognisedNotice(null);
    if (!file) {
      return;
    }
    file
      .text()
      .then((text) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(text) as unknown;
        } catch {
          setImportPresetError('Invalid JSON.');
          return;
        }
        const result = parseUploadedColorPresetJson(parsed);
        if (!result.ok) {
          setImportPresetError(result.error);
          return;
        }

        const { unrecognised } = validateKeys(result.config);
        if (unrecognised.length > 0) {
          const maxShown = 3;
          const shown = unrecognised.slice(0, maxShown);
          const rest = unrecognised.length - shown.length;
          const keys = shown.join(', ');

          setImportUnrecognisedNotice(
            rest > 0
              ? `Unrecognized properties (ignored): ${keys} and ${rest} more`
              : `Unrecognized properties (ignored): ${keys}`,
          );
        } else {
          setImportUnrecognisedNotice(null);
        }
        props.onImportColorPreset(result.config);
      })
      .catch(() => {
        setImportPresetError('Could not read file.');
      })
      .finally(() => {
        input.value = '';
      });
  };

  return (
    <Accordion
      expandedPanels={expandedPanels}
      variant="standard"
      className={styles.accordion}
      data-qa-ref="accordion-color-picker"
    >
      <Accordion.Item className={styles.accordionItem} itemId="logo">
        <Accordion.Header
          onClick={() => handleClick('logo')}
          className={styles.accordionHeader}
          spacing="medium"
        >
          <Accordion.HeaderContent
            withTitle={
              <FormattedMessage
                id=""
                description="Logo Title"
                defaultMessage="Logo"
              />
            }
            className={styles.accordionHeaderContent}
          />
        </Accordion.Header>
        <Accordion.Panel spacing="medium">
          <Text color="faint">
            <FormattedMessage
              id=""
              description="Logo Description"
              defaultMessage="HEIC, PNG, JPG allowed. Recommended width: 64px"
            />
          </Text>
          <input
            type="file"
            accept="image/png, image/jpg, image/jpeg, image/bmp, image/gif"
            aria-label={intl.formatMessage(messages.fileInputMessage)}
            onChange={saveLogo}
          />
        </Accordion.Panel>
      </Accordion.Item>
      {isColorsImportEnabled && (
        <Accordion.Item className={styles.accordionItem} itemId="preset">
          <Accordion.Header
            onClick={() => handleClick('preset')}
            className={styles.accordionHeader}
            spacing="medium"
          >
            <Accordion.HeaderContent
              withTitle={
                <FormattedMessage
                  id=""
                  description="Color preset JSON upload section title"
                  defaultMessage="Color preset (JSON)"
                />
              }
              className={styles.accordionHeaderContent}
            />
          </Accordion.Header>
          <Accordion.Panel spacing="medium">
            <Text color="faint">
              <FormattedMessage
                id=""
                description="Color preset JSON file upload help"
                defaultMessage="Upload a flat .json object of API color keys (e.g. page_background_color, primary_button_color)."
              />
            </Text>
            <input
              type="file"
              accept="application/json,.json"
              aria-label={intl.formatMessage(messages.jsonFileInputMessage)}
              onChange={loadColorPresetJson}
            />
            {importPresetError ? (
              <Text color="error" tagName="p" role="alert">
                {importPresetError}
              </Text>
            ) : null}
            {importUnrecognisedNotice ? (
              <Text color="subtle" tagName="p" role="status">
                {importUnrecognisedNotice}
              </Text>
            ) : null}
          </Accordion.Panel>
        </Accordion.Item>
      )}

      {props.sections.map((section) => (
        <Accordion.Item
          className={styles.accordionItem}
          key={section.sectionId}
          itemId={section.sectionId}
          data-qa-ref="color-picker-section"
        >
          <Accordion.Header
            onClick={() => handleClick(section.sectionId)}
            className={styles.accordionHeader}
            spacing="medium"
          >
            <Accordion.HeaderContent
              withTitle={section.sectionLabel}
              className={styles.accordionHeaderContent}
            />
          </Accordion.Header>
          <Accordion.Panel spacing="medium">
            {section.items.map((item) => (
              <div
                key={item.id}
                className={styles.colorPickerRow}
                data-qa-ref="color-picker-subsection"
              >
                <Text>{item.label}</Text>
                <input
                  className={styles.colorPicker}
                  type="color"
                  value={props.getValue(item.id)}
                  onChange={(e) => props.setValue(item.id, e.target.value)}
                  aria-label={item.label}
                />
              </div>
            ))}
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}

export default AccordionColorPicker;
